import {MilestoneFragment, LazyMilestonePicker, MilestonePickerBase} from '@github-ui/item-picker/MilestonePicker'
import type {
  MilestonePickerMilestone$key,
  MilestonePickerMilestone$data,
} from '@github-ui/item-picker/MilestonePickerMilestone.graphql'
import {ActionList, SelectPanel, type SxProp} from '@primer/react'
import {useMemo, Suspense, useState, type HTMLAttributes} from 'react'
import {graphql, type PreloadedQuery, readInlineData, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import type {MilestonesSectionFragment$key} from './__generated__/MilestonesSectionFragment.graphql'
import {Milestone} from './Milestone'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {noop} from '@github-ui/noop'
import {HOTKEYS} from '@github-ui/item-picker/Hotkeys'
import {useKeyPress} from '@github-ui/use-key-press'
import type {MilestonePickerQuery} from '@github-ui/item-picker/MilestonePickerQuery.graphql'

const ReadonlyMilestonesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.milestones} />
const MilestonesSectionHeader = (props: HTMLAttributes<HTMLButtonElement>) => (
  <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={props} />
)

type MilestonesSectionProps = {
  sectionHeader: JSX.Element
  milestone: MilestonePickerMilestone$data | null
} & SxProp
const MilestonesSection = ({sectionHeader, milestone, sx}: MilestonesSectionProps) => (
  <Section sx={sx} sectionHeader={sectionHeader} emptyText={milestone ? undefined : LABELS.emptySections.milestones}>
    <ActionList variant="full">
      <Milestone milestone={milestone} />
    </ActionList>
  </Section>
)

export type CreateIssueMilestonesSectionProps = {
  activeMilestone: MilestonePickerMilestone$data | null
  milestonesRef: PreloadedQuery<MilestonePickerQuery, Record<string, unknown>>
  onSelectionChange: (milestones: MilestonePickerMilestone$data[]) => void
  viewerCanSetMilestone: boolean
  insidePortal: boolean
  shortcutEnabled: boolean
  shortcutHintVisible: boolean
} & SxProp
export function CreateIssueMilestonesSection({
  activeMilestone,
  milestonesRef,
  onSelectionChange,
  viewerCanSetMilestone,
  sx,
  ...sharedConfigProps
}: CreateIssueMilestonesSectionProps) {
  const [milestone, setMilestone] = useState<MilestonePickerMilestone$data | null>(activeMilestone)

  const sectionHeader = !viewerCanSetMilestone ? (
    <ReadonlyMilestonesSectionHeader />
  ) : (
    <MilestonePickerBase
      activeMilestone={milestone}
      milestoneQueryRef={milestonesRef}
      onSelectionChange={milestones => {
        setMilestone(milestones[0] ?? null)
        onSelectionChange(milestones)
      }}
      anchorElement={MilestonesSectionHeader}
      {...sharedConfigProps}
    />
  )

  return <MilestonesSection sx={sx} sectionHeader={sectionHeader} milestone={milestone} />
}

const MilestonesSectionFragment = graphql`
  fragment MilestonesSectionFragment on Issue {
    repository {
      name
      owner {
        login
      }
    }
    id
    milestone {
      ...MilestonePickerMilestone
    }
    viewerCanSetMilestone
  }
`

type EditIssueMilestonesSectionProps = {
  issue: MilestonesSectionFragment$key
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
}
export function EditIssueMilestonesSection({
  issue,
  onIssueUpdate,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
}: EditIssueMilestonesSectionProps) {
  const {
    repository: {
      owner: {login: owner},
      name: repo,
    },
    id: issueId,
    viewerCanSetMilestone,
    milestone,
  } = useFragment(MilestonesSectionFragment, issue)

  // eslint-disable-next-line no-restricted-syntax
  const activeMilestone = readInlineData<MilestonePickerMilestone$key>(MilestoneFragment, milestone)

  const sectionHeader = useMemo(() => {
    if (!viewerCanSetMilestone) {
      return <ReadonlyMilestonesSectionHeader />
    }
    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlyMilestonesSectionHeader />}>
        <LazyMilestonePicker
          issueId={issueId}
          repo={repo}
          owner={owner}
          readonly={!viewerCanSetMilestone}
          shortcutEnabled={singleKeyShortcutsEnabled}
          activeMilestone={activeMilestone}
          onIssueUpdate={onIssueUpdate}
          anchorElement={MilestonesSectionHeader}
        />
      </Suspense>
    ) : (
      <LazyMilestonePickerOnClick
        issueId={issueId}
        repo={repo}
        owner={owner}
        viewerCanSetMilestone={viewerCanSetMilestone}
        singleKeyShortcutsEnabled={singleKeyShortcutsEnabled}
        activeMilestone={activeMilestone}
        onIssueUpdate={onIssueUpdate}
      />
    )
  }, [
    activeMilestone,
    prefetchInteractionDataEnabled,
    issueId,
    onIssueUpdate,
    owner,
    repo,
    singleKeyShortcutsEnabled,
    viewerCanSetMilestone,
  ])

  return <MilestonesSection sectionHeader={sectionHeader} milestone={activeMilestone} />
}

type LazyMilestonePickerOnClickProps = {
  issueId: string
  repo: string
  owner: string
  viewerCanSetMilestone: boolean
  singleKeyShortcutsEnabled: boolean
  activeMilestone: MilestonePickerMilestone$data | null
  onIssueUpdate?: () => void
}

function LazyMilestonePickerOnClick({
  issueId,
  repo,
  owner,
  viewerCanSetMilestone,
  singleKeyShortcutsEnabled,
  activeMilestone,
  onIssueUpdate,
}: LazyMilestonePickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)
  const [searchText, setSearchText] = useState('')

  useKeyPress(singleKeyShortcutsEnabled ? [HOTKEYS.milestonePicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return <MilestonesSectionHeader onClick={() => setWasTriggered(true)} />
  }
  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          placeholderText={'Filter milestones'}
          open
          onOpenChange={noop}
          items={[]}
          onSelectedChange={noop}
          filterValue={searchText}
          onFilterChange={setSearchText}
          selected={undefined}
          renderAnchor={MilestonesSectionHeader}
          overlayProps={{width: 'medium', height: 'auto'}}
        />
      }
    >
      <LazyMilestonePicker
        issueId={issueId}
        repo={repo}
        owner={owner}
        readonly={!viewerCanSetMilestone}
        shortcutEnabled={singleKeyShortcutsEnabled}
        activeMilestone={activeMilestone}
        onIssueUpdate={onIssueUpdate}
        triggerOpen={true}
        initialFilter={searchText}
        anchorElement={MilestonesSectionHeader}
      />
    </Suspense>
  )
}

try{ ReadonlyMilestonesSectionHeader.displayName ||= 'ReadonlyMilestonesSectionHeader' } catch {}
try{ MilestonesSectionHeader.displayName ||= 'MilestonesSectionHeader' } catch {}
try{ MilestonesSection.displayName ||= 'MilestonesSection' } catch {}
try{ CreateIssueMilestonesSection.displayName ||= 'CreateIssueMilestonesSection' } catch {}
try{ EditIssueMilestonesSection.displayName ||= 'EditIssueMilestonesSection' } catch {}
try{ LazyMilestonePickerOnClick.displayName ||= 'LazyMilestonePickerOnClick' } catch {}